import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { DoctorFinderBar } from "../appointment/DoctorFinderBar";
import Fade from "@mui/material/Fade";
import Divider from "@mui/material/Divider";
import GlobalStyles from "@mui/material/GlobalStyles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUnifiedStore } from "../../store/StoreContext";
import { useSystemTypeConfig } from "../../hooks/useSystemTypeConfig";
import { useSlotsQuery } from "../../queries/useSlotsQuery";
import { usePatientsQuery } from "../../queries/usePatientsQuery";
import { usePaymentMethodsQuery } from "../../queries/usePaymentMethodsQuery";
import { useFollowUpEligibilityQuery } from "../../queries/useFollowUpEligibilityQuery";
import { usePricingCountry } from "../../store/pricingCountry";
import { planQuotesFrom, quoteCharge } from "../../store/slices/chargeQuote";
import { bookedChargeAmount } from "../../store/slices/bookedAppointment";
import { FollowUpOffer } from "./FollowUpOffer";
import { PaymentMethodPicker } from "./PaymentMethodPicker";
import { PaymentClaimSheet } from "./PaymentClaimSheet";
import { SchedulePanel } from "../appointment/SchedulePanel";
import { PhoneStep } from "../appointment/PhoneStep";
import { OtpStep } from "../appointment/OtpStep";
import { PatientSelectionStep } from "../appointment/PatientSelectionStep";
import { SummaryStep } from "../appointment/SummaryStep";
import { SuccessStep } from "../appointment/SuccessStep";
import { BottomBar } from "../appointment/BottomBar";
import { DoctorCard } from "../appointment/DoctorCard";
import { DoctorSquareCard } from "../appointment/DoctorSquareCard";
import { StepIndicator } from "../appointment/StepIndicator";
import { getDisplayForDoctor } from "../appointment/utils/doctorDisplay";
import { getAddressesWithDoctors, getDoctorConsultationModes, getVisibleLocations, getVisibleDoctorsForAddress, findFirstVisibleDoctorInAddress, doctorSupportsConsultationMode, getDoctorFeeAudienceOptions, preferredFeeAudience, } from "../appointment/utils/workspaceDoctors";
import { isPackAvailableForMode, isOwnedPackAvailableForDuration, isOwnedPackAllowedForPatient, } from "../appointment/utils/sessionPacks";
import { LOCATION_UNSELECTED } from "../../store/slices/doctorSelectionSlice";
import { QueueConfirmationStep } from "./QueueConfirmationStep";
import { PaymentPendingStep } from "./PaymentPendingStep";
import { WidgetHeader } from "../shared/WidgetHeader";
import { TimezoneChangeDialog } from "../shared/TimezoneChangeDialog";
import { BookingHeroHeader } from "./BookingHeroHeader";
import { SessionPacksSection } from "../appointment/SessionPacksSection";
import { getHeaderBackHandler } from "../appointment/utils/headerActions";
import { useTranslation } from "../../hooks/useTranslation";
import { resolveAppointmentSystemType } from "../../utils/resolveAppointmentSystemType";
import { isAppointmentSystemTypeEnabled, resolveWorkspaceFeatureAccess, } from "../../utils/workspaceFeatures";
import { DEFAULT_WIDGET_LAYOUT, resolveDoctorListPlacement, } from "../../utils/widgetLayout";
const DOCTOR_LIST_COLUMN_WIDTH = 320;
const EMPTY_NUMBER_LIST = [];
const EMPTY_PLAN_LIST = [];
const globalStyles = {
    ".medos-booking-widget": { scrollbarWidth: "thin" },
    ".medos-hscroll::-webkit-scrollbar": { display: "none" },
    ".medos-hscroll": {
        scrollbarWidth: "none",
        msOverflowStyle: "none",
    },
    ".medos-booking-widget *::-webkit-scrollbar": { width: 6, height: 6 },
    ".medos-booking-widget *::-webkit-scrollbar-track": {
        background: "transparent",
    },
    ".medos-booking-widget *::-webkit-scrollbar-thumb": {
        background: "rgba(156, 163, 175, 0.5)",
        borderRadius: 4,
    },
};
export function WidgetShell({ maxWidth, maxHeight, height, calendarOnly = false, compact = false, layout = DEFAULT_WIDGET_LAYOUT, onError, onSuccess, }) {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const { t } = useTranslation("appointment");
    const workspace = useUnifiedStore((s) => s.workspace);
    const workspaceLoading = useUnifiedStore((s) => s.workspaceLoading);
    const workspaceError = useUnifiedStore((s) => s.workspaceError);
    const visibleDoctorIds = useUnifiedStore((s) => s.visibleDoctorIds);
    const retryLoad = useUnifiedStore((s) => s.retryLoad);
    const selectedDoctor = useUnifiedStore((s) => s.selectedDoctor);
    const selectedAddressId = useUnifiedStore((s) => s.selectedAddressId);
    const geoLocation = useUnifiedStore((s) => s.geoLocation);
    const activeSystemType = useUnifiedStore((s) => s.activeSystemType);
    const doctorDisplayByDoctorId = useUnifiedStore((s) => s.doctorDisplayByDoctorId);
    const currentStep = useUnifiedStore((s) => s.currentStep);
    const selectedDate = useUnifiedStore((s) => s.selectedDate);
    const selectedSlot = useUnifiedStore((s) => s.selectedSlot);
    const selectedDuration = useUnifiedStore((s) => s.selectedDuration);
    const selectedTimezone = useUnifiedStore((s) => s.selectedTimezone);
    const detectedTimezone = useUnifiedStore((s) => s.detectedTimezone);
    const pendingTimezone = useUnifiedStore((s) => s.pendingTimezone);
    const selectedPack = useUnifiedStore((s) => s.selectedPack);
    const showAllPacks = useUnifiedStore((s) => s.showAllPacks);
    const availablePacks = useUnifiedStore((s) => s.availablePacks);
    const consultationMode = useUnifiedStore((s) => s.consultationMode);
    const feeAudience = useUnifiedStore((s) => s.feeAudience);
    const pricingRefreshPending = useUnifiedStore((s) => s.pricingRefreshPending);
    const searchQuery = useUnifiedStore((s) => s.searchQuery);
    const specializationFilter = useUnifiedStore((s) => s.specializationFilter);
    const locationFilter = useUnifiedStore((s) => s.locationFilter);
    const otpChannel = useUnifiedStore((s) => s.otpChannel);
    const otpChannels = useUnifiedStore((s) => s.otpChannels);
    const countryCode = useUnifiedStore((s) => s.countryCode);
    const patientPhone = useUnifiedStore((s) => s.patientPhone);
    const patientEmail = useUnifiedStore((s) => s.patientEmail);
    const otpCode = useUnifiedStore((s) => s.otpCode);
    const otpSent = useUnifiedStore((s) => s.otpSent);
    const otpVerified = useUnifiedStore((s) => s.otpVerified);
    const otpSending = useUnifiedStore((s) => s.otpSending);
    const otpVerifying = useUnifiedStore((s) => s.otpVerifying);
    const otpError = useUnifiedStore((s) => s.otpError);
    const verifiedPatients = useUnifiedStore((s) => s.verifiedPatients);
    const activeSessionPacks = useUnifiedStore((s) => s.activeSessionPacks);
    const patientsLoading = useUnifiedStore((s) => s.patientsLoading);
    const patientTab = useUnifiedStore((s) => s.patientTab);
    const selectedPatient = useUnifiedStore((s) => s.selectedPatient);
    const selectedActiveSessionPack = useUnifiedStore((s) => s.selectedActiveSessionPack);
    const newPatientForm = useUnifiedStore((s) => s.newPatientForm);
    const bookingLoading = useUnifiedStore((s) => s.bookingLoading);
    const bookingError = useUnifiedStore((s) => s.bookingError);
    const appointmentId = useUnifiedStore((s) => s.appointmentId);
    const queueToken = useUnifiedStore((s) => s.queueToken);
    const bookedAppointment = useUnifiedStore((s) => s.bookedAppointment);
    const bookingRevision = useUnifiedStore((s) => s.bookingRevision);
    const paymentStatus = useUnifiedStore((s) => s.paymentStatus);
    const paymentSessionRef = useUnifiedStore((s) => s.paymentSessionRef);
    const paymentCheckoutUrl = useUnifiedStore((s) => s.paymentCheckoutUrl);
    const paymentUnavailable = useUnifiedStore((s) => s.paymentUnavailable);
    const paymentPopup = useUnifiedStore((s) => s.paymentPopup);
    const paymentError = useUnifiedStore((s) => s.paymentError);
    const paymentFailureReason = useUnifiedStore((s) => s.paymentFailureReason);
    const claimStatus = useUnifiedStore((s) => s.claimStatus);
    const paymentAppointmentRef = useUnifiedStore((s) => s.paymentAppointmentRef);
    const recheckOnlinePayment = useUnifiedStore((s) => s.recheckOnlinePayment);
    const followUpEligibility = useUnifiedStore((s) => s.followUpEligibility);
    const followUpSelected = useUnifiedStore((s) => s.followUpSelected);
    const followUpGrid = useUnifiedStore((s) => s.followUpGrid);
    const setFollowUpEligibility = useUnifiedStore((s) => s.setFollowUpEligibility);
    const leaveFollowUpGrid = useUnifiedStore((s) => s.leaveFollowUpGrid);
    const feeAudienceOptions = useMemo(() => selectedDoctor
        ? getDoctorFeeAudienceOptions(selectedDoctor, consultationMode)
        : [], [selectedDoctor, consultationMode]);
    const selectedAddress = useMemo(() => workspace?.addresses.find((a) => a.id === selectedAddressId) ?? null, [workspace, selectedAddressId]);
    const visitorFeeAudience = useMemo(() => preferredFeeAudience(selectedAddress?.international), [selectedAddress]);
    const selectDoctor = useUnifiedStore((s) => s.selectDoctor);
    const clearSelectedDoctor = useUnifiedStore((s) => s.clearSelectedDoctor);
    const setSelectedDate = useUnifiedStore((s) => s.setSelectedDate);
    const setSelectedSlot = useUnifiedStore((s) => s.setSelectedSlot);
    const requestTimezoneChange = useUnifiedStore((s) => s.requestTimezoneChange);
    const confirmTimezoneChange = useUnifiedStore((s) => s.confirmTimezoneChange);
    const cancelTimezoneChange = useUnifiedStore((s) => s.cancelTimezoneChange);
    const setSelectedPack = useUnifiedStore((s) => s.setSelectedPack);
    const setShowAllPacks = useUnifiedStore((s) => s.setShowAllPacks);
    const setAvailablePacks = useUnifiedStore((s) => s.setAvailablePacks);
    const setPlanQuotes = useUnifiedStore((s) => s.setPlanQuotes);
    const setSelectedDuration = useUnifiedStore((s) => s.setSelectedDuration);
    const setConsultationMode = useUnifiedStore((s) => s.setConsultationMode);
    const setFeeAudience = useUnifiedStore((s) => s.setFeeAudience);
    const setSearchQuery = useUnifiedStore((s) => s.setSearchQuery);
    const setSpecializationFilter = useUnifiedStore((s) => s.setSpecializationFilter);
    const setLocationFilter = useUnifiedStore((s) => s.setLocationFilter);
    const setOtpChannel = useUnifiedStore((s) => s.setOtpChannel);
    const setCountryCode = useUnifiedStore((s) => s.setCountryCode);
    const setPatientPhone = useUnifiedStore((s) => s.setPatientPhone);
    const setPatientEmail = useUnifiedStore((s) => s.setPatientEmail);
    const setOtpCode = useUnifiedStore((s) => s.setOtpCode);
    const setOtpError = useUnifiedStore((s) => s.setOtpError);
    const sendOtp = useUnifiedStore((s) => s.sendOtp);
    const verifyOtp = useUnifiedStore((s) => s.verifyOtp);
    const resetOtp = useUnifiedStore((s) => s.resetOtp);
    const setPatientTab = useUnifiedStore((s) => s.setPatientTab);
    const setSelectedPatient = useUnifiedStore((s) => s.setSelectedPatient);
    const setSelectedActiveSessionPack = useUnifiedStore((s) => s.setSelectedActiveSessionPack);
    const updateNewPatientForm = useUnifiedStore((s) => s.updateNewPatientForm);
    const continueToSummary = useUnifiedStore((s) => s.continueToSummary);
    const submitBooking = useUnifiedStore((s) => s.submitBooking);
    const resetBooking = useUnifiedStore((s) => s.resetBooking);
    const setBookingError = useUnifiedStore((s) => s.setBookingError);
    const setStep = useUnifiedStore((s) => s.setStep);
    const goBack = useUnifiedStore((s) => s.goBack);
    const startOnlinePayment = useUnifiedStore((s) => s.startOnlinePayment);
    const resumeOnlinePayment = useUnifiedStore((s) => s.resumeOnlinePayment);
    const finishOnlinePayment = useUnifiedStore((s) => s.finishOnlinePayment);
    const resetOnlinePayment = useUnifiedStore((s) => s.resetOnlinePayment);
    const setPaymentPopup = useUnifiedStore((s) => s.setPaymentPopup);
    const baseConfig = useSystemTypeConfig();
    const featureAccess = useMemo(() => resolveWorkspaceFeatureAccess(workspace), [workspace]);
    const config = useMemo(() => ({
        ...baseConfig,
        showQueueStats: baseConfig.showQueueStats && featureAccess.qms,
        showSessionPacks: baseConfig.showSessionPacks && featureAccess.appointmentSessionPacks,
    }), [baseConfig, featureAccess]);
    const onlinePaymentAvailable = !!workspace?.onlinePaymentEnabled &&
        featureAccess.onlinePayment &&
        !paymentUnavailable;
    const dateStr = useMemo(() => {
        const y = String(selectedDate.getFullYear()).padStart(4, "0");
        const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const d = String(selectedDate.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }, [selectedDate]);
    const pricingCountryCode = usePricingCountry();
    const slotsQuery = useSlotsQuery({
        workspaceId: workspace?.workspaceId ?? null,
        addressId: selectedAddressId,
        doctorId: selectedDoctor?.id ?? null,
        date: dateStr,
        slotFetchMode: config.slotFetchMode,
        invalidationKey: bookingRevision,
        patientTimezone: selectedTimezone,
        durationMins: followUpGrid ? null : selectedDuration,
        mode: selectedDoctor &&
            doctorSupportsConsultationMode(selectedDoctor, consultationMode)
            ? consultationMode
            : null,
        followUp: followUpGrid,
        patientCountryCode: pricingCountryCode,
    });
    const availableDurations = slotsQuery.data?.availableDurations ?? EMPTY_NUMBER_LIST;
    const slotPlans = slotsQuery.data?.plans ?? EMPTY_PLAN_LIST;
    const effectiveDuration = followUpGrid
        ? (slotsQuery.data?.gridDurationMins ?? null)
        : selectedDuration;
    const refetchSlots = slotsQuery.refetch;
    const slotsQueryEnabled = !!(workspace?.workspaceId &&
        selectedAddressId &&
        selectedDoctor?.id);
    const slotGridMissing = slotsQueryEnabled && !slotsQuery.data;
    const planQuotes = useMemo(() => planQuotesFrom(slotPlans, effectiveDuration, slotsQuery.data?.queueCurrency), [slotPlans, effectiveDuration, slotsQuery.data?.queueCurrency]);
    React.useEffect(() => {
        setPlanQuotes(planQuotes);
    }, [planQuotes, setPlanQuotes]);
    React.useEffect(() => {
        if (currentStep !== "schedule" ||
            followUpGrid ||
            !slotsQuery.isSuccess) {
            return;
        }
        if (availableDurations.length === 0) {
            if (selectedDuration !== null)
                setSelectedDuration(null);
            return;
        }
        if (selectedDuration === null ||
            !availableDurations.includes(selectedDuration)) {
            setSelectedDuration(availableDurations[0]);
        }
    }, [
        currentStep,
        followUpGrid,
        availableDurations,
        selectedDuration,
        setSelectedDuration,
        slotsQuery.isSuccess,
    ]);
    React.useEffect(() => {
        if (currentStep !== "schedule" || !slotsQuery.isSuccess)
            return;
        const slots = slotsQuery.data?.slots ?? [];
        const next = (selectedSlot &&
            slots.find((slot) => slot.start === selectedSlot.start)) ??
            slots[0] ??
            null;
        if (next !== selectedSlot)
            setSelectedSlot(next);
    }, [
        currentStep,
        selectedSlot,
        setSelectedSlot,
        slotsQuery.data?.slots,
        slotsQuery.isSuccess,
    ]);
    const previousStepRef = React.useRef(currentStep);
    React.useEffect(() => {
        const previous = previousStepRef.current;
        previousStepRef.current = currentStep;
        if (currentStep === "schedule" &&
            previous !== "schedule" &&
            slotsQueryEnabled) {
            void refetchSlots({ cancelRefetch: false });
        }
    }, [currentStep, refetchSlots, slotsQueryEnabled]);
    React.useEffect(() => {
        if (!selectedPack)
            return;
        if (!slotsQuery.isSuccess)
            return;
        if (planQuotes.packs[String(selectedPack.id)])
            return;
        setSelectedPack(null);
    }, [planQuotes, selectedPack, setSelectedPack, slotsQuery.isSuccess]);
    const patientLookup = React.useMemo(() => otpChannel === "email"
        ? { channel: "email", email: patientEmail }
        : {
            channel: "phone",
            countryCode,
            phoneNumber: patientPhone,
        }, [otpChannel, countryCode, patientPhone, patientEmail]);
    const patientsQuery = usePatientsQuery({
        lookup: patientLookup,
        enabled: otpVerified,
        bookingRevision,
    });
    const setPatientsLoading = useUnifiedStore((s) => s.setPatientsLoading);
    const setVerifiedData = useUnifiedStore((s) => s.setVerifiedData);
    React.useEffect(() => {
        setPatientsLoading(patientsQuery.isLoading);
    }, [patientsQuery.isLoading, setPatientsLoading]);
    React.useEffect(() => {
        if (patientsQuery.data) {
            const patients = patientsQuery.data.data?.patients ?? [];
            const packs = featureAccess.packageUsage
                ? (patientsQuery.data.data?.activeSessionPacks ?? [])
                : [];
            setVerifiedData(patients, packs);
        }
    }, [patientsQuery.data, featureAccess.packageUsage, setVerifiedData]);
    const followUpEligibilityQuery = useFollowUpEligibilityQuery({
        doctorId: selectedDoctor?.id ?? null,
        addressId: selectedAddressId,
        mode: consultationMode,
        date: dateStr,
        enabled: otpVerified &&
            config.bookingMode !== "qms" &&
            verifiedPatients.length > 0,
        bookingRevision,
    });
    React.useEffect(() => {
        setFollowUpEligibility(followUpEligibilityQuery.data ?? null);
    }, [followUpEligibilityQuery.data, setFollowUpEligibility]);
    const quote = useMemo(() => quoteCharge({
        workspace,
        activeSystemType,
        feeAudience: visitorFeeAudience,
        planQuotes,
        selectedSlot,
        selectedPack,
        selectedActiveSessionPack,
        followUpEligibility,
        followUpSelected,
        patientTab,
        selectedPatient,
    }), [
        workspace,
        activeSystemType,
        visitorFeeAudience,
        planQuotes,
        selectedSlot,
        selectedPack,
        selectedActiveSessionPack,
        followUpEligibility,
        followUpSelected,
        patientTab,
        selectedPatient,
    ]);
    const pricePending = slotGridMissing || pricingRefreshPending;
    const ownedPackMayPay = featureAccess.packageUsage &&
        config.bookingMode !== "qms" &&
        (!otpVerified || activeSessionPacks.length > 0);
    const unquotedAndUnpayable = quote === null && !ownedPackMayPay;
    const scheduleBookBlocked = pricePending || unquotedAndUnpayable;
    const summaryBookBlocked = quote?.kind !== "ACTIVE_PACK" && (pricePending || quote === null);
    const paymentMethodsQuery = usePaymentMethodsQuery(Boolean(workspace));
    const setAvailablePaymentMethods = useUnifiedStore((s) => s.setAvailablePaymentMethods);
    React.useEffect(() => {
        setAvailablePaymentMethods(paymentMethodsQuery.data ?? []);
    }, [paymentMethodsQuery.data, setAvailablePaymentMethods]);
    React.useEffect(() => {
        if (!featureAccess.appointmentSessionPacks) {
            setAvailablePacks([]);
            return;
        }
        const packs = slotsQuery.data?.allSessionPackResponses;
        if (packs)
            setAvailablePacks(packs);
    }, [
        slotsQuery.data,
        featureAccess.appointmentSessionPacks,
        setAvailablePacks,
    ]);
    const specializationsFor = React.useCallback((doctor) => getDisplayForDoctor(doctor, doctorDisplayByDoctorId).specializations, [doctorDisplayByDoctorId]);
    const filteredDoctors = useMemo(() => {
        if (!workspace)
            return [];
        const addressId = locationFilter ? Number(locationFilter) : NaN;
        if (Number.isNaN(addressId))
            return [];
        const address = getAddressesWithDoctors(workspace).find((a) => a.id === addressId);
        if (!address)
            return [];
        const query = searchQuery.toLowerCase().trim();
        const visible = visibleDoctorIds !== null ? new Set(visibleDoctorIds) : null;
        const results = [];
        for (const doctor of address.doctors) {
            if (visible && !visible.has(doctor.id))
                continue;
            if (!isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(doctor, workspace))) {
                continue;
            }
            const specializations = specializationsFor(doctor);
            if (query) {
                const name = getDisplayForDoctor(doctor, doctorDisplayByDoctorId).name;
                const nameMatch = name.toLowerCase().includes(query);
                const specMatch = specializations.some((s) => s.toLowerCase().includes(query));
                if (!nameMatch && !specMatch)
                    continue;
            }
            if (specializationFilter !== "all" &&
                !specializations.includes(specializationFilter))
                continue;
            results.push({ doctor, addressId: address.id, city: address.city });
        }
        return results;
    }, [
        workspace,
        searchQuery,
        specializationFilter,
        locationFilter,
        visibleDoctorIds,
        doctorDisplayByDoctorId,
        specializationsFor,
    ]);
    const selectedDoctorDisplay = selectedDoctor
        ? getDisplayForDoctor(selectedDoctor, doctorDisplayByDoctorId)
        : null;
    const bridgedState = {
        loading: workspaceLoading,
        error: workspaceError,
        workspace,
        geoLocation,
        consultationMode,
        searchQuery,
        specializationFilter,
        locationFilter,
        selectedDoctor,
        selectedAddressId,
        selectedDate,
        selectedTimezone,
        selectedSlot,
        selectedPack,
        slots: slotsQuery.data?.slots ?? [],
        slotsLoading: slotsQuery.isLoading,
        slotsError: slotsQuery.error
            ? slotsQuery.error?.response?.data?.message ||
                slotsQuery.error.message ||
                "Failed to load slots"
            : null,
        slotsVersion: 0,
        showAllPacks,
        planQuotes,
        quote,
        pricePending,
        currentStep: currentStep,
        otpChannel,
        otpChannels,
        countryCode,
        patientPhone,
        patientEmail,
        otpCode,
        otpSent,
        otpVerified,
        otpSending,
        otpVerifying,
        otpError,
        verifiedPatients,
        availablePacks,
        activeSessionPacks,
        patientsLoading,
        patientTab,
        selectedPatient,
        selectedActiveSessionPack,
        newPatientForm,
        bookingFormSchema: null,
        bookingFormValues: {},
        bookingFormLoading: false,
        selectedDoctorDisplay,
        doctorDisplayByDoctorId,
        bookingLoading,
        bookingError,
        reservationId: null,
        paymentOrderId: null,
        paymentStatus: "idle",
        appointmentId,
    };
    const dispatch = React.useCallback((action) => {
        switch (action.type) {
            case "SET_SELECTED_DATE":
                setSelectedDate(action.payload);
                break;
            case "SET_SELECTED_SLOT":
                setSelectedSlot(action.payload);
                break;
            case "SET_TIMEZONE":
                requestTimezoneChange(action.payload);
                break;
            case "SET_SELECTED_PACK":
                setSelectedPack(action.payload);
                break;
            case "SET_SHOW_ALL_PACKS":
                setShowAllPacks(action.payload);
                break;
            case "SET_CONSULTATION_MODE":
                setConsultationMode(action.payload);
                break;
            case "SET_SEARCH_QUERY":
                setSearchQuery(action.payload);
                break;
            case "SET_SPECIALIZATION_FILTER":
                setSpecializationFilter(action.payload);
                break;
            case "SET_LOCATION_FILTER":
                setLocationFilter(action.payload);
                break;
            case "SET_OTP_CHANNEL":
                setOtpChannel(action.payload);
                break;
            case "SET_COUNTRY_CODE":
                setCountryCode(action.payload);
                break;
            case "SET_PATIENT_PHONE":
                setPatientPhone(action.payload);
                break;
            case "SET_PATIENT_EMAIL":
                setPatientEmail(action.payload);
                break;
            case "SET_OTP_CODE":
                setOtpCode(action.payload);
                break;
            case "SET_OTP_ERROR":
                setOtpError(action.payload);
                break;
            case "SET_PATIENT_TAB":
                setPatientTab(action.payload);
                break;
            case "SET_SELECTED_PATIENT":
                setSelectedPatient(action.payload);
                break;
            case "SET_SELECTED_ACTIVE_SESSION_PACK":
                setSelectedActiveSessionPack(action.payload);
                break;
            case "SET_NEW_PATIENT_FORM":
                updateNewPatientForm(action.payload);
                break;
            case "SET_BOOKING_ERROR":
                setBookingError(action.payload);
                break;
            case "SELECT_DOCTOR":
                selectDoctor(action.payload.doctor, action.payload.addressId);
                break;
            case "RESET_OTP":
                resetOtp();
                break;
            case "RESET_BOOKING":
                resetBooking();
                break;
            case "SET_CURRENT_STEP":
                setStep(action.payload);
                break;
            default:
                break;
        }
    }, [
        setSelectedDate,
        setSelectedSlot,
        requestTimezoneChange,
        setSelectedPack,
        setShowAllPacks,
        setConsultationMode,
        setSearchQuery,
        setSpecializationFilter,
        setLocationFilter,
        setCountryCode,
        setPatientPhone,
        setOtpCode,
        setPatientTab,
        setSelectedPatient,
        setSelectedActiveSessionPack,
        updateNewPatientForm,
        setBookingError,
        selectDoctor,
        resetOtp,
        resetBooking,
        setStep,
    ]);
    const handleBook = React.useCallback(() => {
        if (scheduleBookBlocked)
            return;
        if (otpVerified) {
            setStep("patient-selection");
        }
        else {
            setStep("phone");
        }
    }, [scheduleBookBlocked, otpVerified, setStep]);
    const handleSendOtp = React.useCallback(() => {
        sendOtp();
    }, [sendOtp]);
    const handleVerifyOtp = React.useCallback(() => {
        verifyOtp();
    }, [verifyOtp]);
    const handleChangeNumber = React.useCallback(() => {
        resetOtp();
        setStep("phone");
    }, [resetOtp, setStep]);
    const continueFromForm = React.useCallback(() => {
        setStep("patient-selection");
    }, [setStep]);
    const handlePayAtClinic = React.useCallback(() => {
        submitBooking();
    }, [submitBooking]);
    const handleConfirmAppointment = React.useCallback(() => {
        submitBooking();
    }, [submitBooking]);
    const handlePayOnline = React.useCallback(() => {
        void startOnlinePayment();
    }, [startOnlinePayment]);
    const handleBookAnother = React.useCallback(() => {
        resetBooking();
    }, [resetBooking]);
    const handleRetryOnlinePayment = React.useCallback(() => {
        if (paymentSessionRef && paymentCheckoutUrl) {
            resumeOnlinePayment();
        }
        else {
            void startOnlinePayment();
        }
    }, [
        paymentSessionRef,
        paymentCheckoutUrl,
        resumeOnlinePayment,
        startOnlinePayment,
    ]);
    const handleCancelOnlinePayment = React.useCallback(() => {
        resetOnlinePayment();
        setStep("summary");
    }, [resetOnlinePayment, setStep]);
    const handleReopenCheckout = React.useCallback(() => {
        if (!paymentCheckoutUrl || typeof window === "undefined")
            return;
        try {
            const popup = window.open(paymentCheckoutUrl, "medos_online_payment", "width=520,height=720,scrollbars=yes,resizable=yes");
            if (popup)
                setPaymentPopup(popup);
        }
        catch {
        }
    }, [paymentCheckoutUrl, setPaymentPopup]);
    const selectedDoctorCity = useMemo(() => {
        if (!workspace || !selectedAddressId)
            return "";
        return (workspace.addresses.find((a) => a.id === selectedAddressId)?.city ?? "");
    }, [workspace, selectedAddressId]);
    const visibleLocations = useMemo(() => {
        if (!workspace)
            return [];
        const visible = visibleDoctorIds !== null ? new Set(visibleDoctorIds) : null;
        return getVisibleLocations(workspace, visibleDoctorIds).filter((location) => {
            const address = workspace.addresses.find((item) => item.id === location.id);
            return address?.doctors.some((doctor) => (!visible || visible.has(doctor.id)) &&
                isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(doctor, workspace)));
        });
    }, [workspace, visibleDoctorIds]);
    const totalVisibleDoctors = useMemo(() => {
        if (!workspace)
            return 0;
        const visible = visibleDoctorIds !== null ? new Set(visibleDoctorIds) : null;
        const seen = new Set();
        for (const a of workspace.addresses) {
            for (const d of a.doctors) {
                if ((!visible || visible.has(d.id)) &&
                    isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(d, workspace))) {
                    seen.add(d.id);
                }
            }
        }
        return seen.size;
    }, [workspace, visibleDoctorIds]);
    const hasMultipleLocations = visibleLocations.length > 1;
    const doctorConsultationModes = useMemo(() => (selectedDoctor ? getDoctorConsultationModes(selectedDoctor) : []), [selectedDoctor]);
    React.useEffect(() => {
        if (visitorFeeAudience === feeAudience)
            return;
        setFeeAudience(visitorFeeAudience);
    }, [visitorFeeAudience, feeAudience, setFeeAudience]);
    const showConsultationToggle = doctorConsultationModes.length > 1;
    const doctorHasNoBookableMode = !!selectedDoctor && doctorConsultationModes.length === 0;
    const doctorModeUnpriced = !!selectedDoctor &&
        !doctorHasNoBookableMode &&
        feeAudienceOptions.length === 0;
    React.useEffect(() => {
        if (doctorConsultationModes.length === 0)
            return;
        if (doctorConsultationModes.includes(consultationMode))
            return;
        setConsultationMode(doctorConsultationModes[0]);
    }, [doctorConsultationModes, consultationMode, setConsultationMode]);
    React.useEffect(() => {
        if (!selectedPack)
            return;
        if (isPackAvailableForMode(selectedPack, consultationMode))
            return;
        setSelectedPack(null);
    }, [selectedPack, consultationMode, setSelectedPack]);
    React.useEffect(() => {
        if (!selectedActiveSessionPack)
            return;
        const stillUsable = isPackAvailableForMode(selectedActiveSessionPack, consultationMode) &&
            isOwnedPackAvailableForDuration(selectedActiveSessionPack, effectiveDuration) &&
            isOwnedPackAllowedForPatient(selectedActiveSessionPack, patientTab, selectedPatient);
        if (stillUsable)
            return;
        setSelectedActiveSessionPack(null);
    }, [
        selectedActiveSessionPack,
        consultationMode,
        effectiveDuration,
        patientTab,
        selectedPatient,
        setSelectedActiveSessionPack,
    ]);
    const locationChosen = locationFilter !== LOCATION_UNSELECTED;
    const noVisibleDoctors = totalVisibleDoctors === 0;
    const isSolo = totalVisibleDoctors <= 1;
    const isScheduleStep = currentStep === "schedule";
    const doctorListPlacement = resolveDoctorListPlacement({
        layout,
        isScheduleStep,
        calendarOnly,
        compact,
        isMobile,
        isSolo,
        hasWorkspace: !!workspace,
        locationChosen,
        noVisibleDoctors,
    });
    const isSideBySide = doctorListPlacement === "side";
    const showProfilePanel = isScheduleStep &&
        !calendarOnly &&
        !compact &&
        !isMobile &&
        !isSideBySide &&
        !!selectedDoctor &&
        !!selectedDoctorDisplay;
    const showProfileHeaderBand = isSideBySide && !compact && !!selectedDoctor && !!selectedDoctorDisplay;
    const packsOwnedByCard = showProfilePanel || isSideBySide;
    const packsToDisplay = featureAccess.appointmentSessionPacks
        ? availablePacks && availablePacks.length > 0
            ? availablePacks
            : (workspace?.sessionPacks ?? [])
        : [];
    const packsElement = config.showSessionPacks &&
        !followUpGrid &&
        packsToDisplay.length > 0 &&
        selectedDoctor &&
        !doctorHasNoBookableMode &&
        !doctorModeUnpriced ? (_jsx(SessionPacksSection, { packs: packsToDisplay, selectedPack: selectedPack, selectedDoctor: selectedDoctor, selectedPatient: selectedPatient, patientTab: patientTab, activeSessionPacks: activeSessionPacks, singleVisit: planQuotes.singleVisit, packQuotes: planQuotes.packs, showAll: showAllPacks, consultationMode: consultationMode, dispatch: dispatch })) : null;
    const specializationOptions = useMemo(() => {
        if (!workspace || !locationChosen)
            return [];
        const doctors = getVisibleDoctorsForAddress(workspace, Number(locationFilter), visibleDoctorIds);
        const set = new Set();
        for (const d of doctors) {
            if (!isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(d, workspace))) {
                continue;
            }
            for (const s of specializationsFor(d))
                set.add(s);
        }
        return [...set];
    }, [workspace, locationFilter, visibleDoctorIds, specializationsFor]);
    const showSpecializationFilter = specializationOptions.length > 1;
    const handleSelectLocation = React.useCallback((addressId) => {
        setLocationFilter(String(addressId));
        setSpecializationFilter("all");
        if (!workspace)
            return;
        const doctor = findFirstVisibleDoctorInAddress(workspace, addressId, visibleDoctorIds, consultationMode);
        if (doctor &&
            isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(doctor, workspace))) {
            selectDoctor(doctor, addressId);
        }
        else {
            clearSelectedDoctor();
        }
    }, [
        workspace,
        visibleDoctorIds,
        consultationMode,
        setLocationFilter,
        setSpecializationFilter,
        selectDoctor,
        clearSelectedDoctor,
    ]);
    const handleSelectSpecialization = React.useCallback((value) => {
        setSpecializationFilter(value);
        if (!workspace)
            return;
        const stillMatches = (value === "all" ||
            (selectedDoctor
                ? specializationsFor(selectedDoctor).includes(value)
                : false)) &&
            (!selectedDoctor ||
                doctorSupportsConsultationMode(selectedDoctor, consultationMode));
        if (stillMatches)
            return;
        const addressId = Number(locationFilter);
        const candidates = getVisibleDoctorsForAddress(workspace, addressId, visibleDoctorIds).filter((d) => (value === "all" || specializationsFor(d).includes(value)) &&
            isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(d, workspace)));
        const modeMatch = candidates.find((d) => doctorSupportsConsultationMode(d, consultationMode));
        const match = modeMatch ?? candidates[0];
        if (match)
            selectDoctor(match, addressId);
    }, [
        workspace,
        locationFilter,
        visibleDoctorIds,
        selectedDoctor,
        consultationMode,
        specializationsFor,
        setSpecializationFilter,
        selectDoctor,
    ]);
    const deselectDoctor = React.useCallback(() => {
        clearSelectedDoctor();
    }, [clearSelectedDoctor]);
    const paymentBackBlocked = currentStep === "payment-pending" &&
        (paymentStatus === "reserving" || paymentStatus === "awaiting");
    const headerBackHandler = currentStep === "schedule" || paymentBackBlocked
        ? undefined
        : getHeaderBackHandler(bridgedState, {
            isSolo,
            calendarOnly,
            showDoctorPanel: true,
            goBack,
            deselectDoctor,
        });
    React.useEffect(() => {
        if (currentStep === "success" || currentStep === "queue-confirmation") {
            return;
        }
        if (!workspace || !selectedDoctor)
            return;
        if (isAppointmentSystemTypeEnabled(workspace, resolveAppointmentSystemType(selectedDoctor, workspace))) {
            return;
        }
        const fallback = filteredDoctors[0];
        if (fallback) {
            selectDoctor(fallback.doctor, fallback.addressId);
        }
        else {
            clearSelectedDoctor();
        }
    }, [
        currentStep,
        workspace,
        selectedDoctor,
        filteredDoctors,
        selectDoctor,
        clearSelectedDoctor,
    ]);
    React.useEffect(() => {
        if (currentStep !== "schedule" ||
            selectedDoctor ||
            filteredDoctors.length === 0) {
            return;
        }
        const { doctor, addressId } = filteredDoctors[0];
        selectDoctor(doctor, addressId);
    }, [currentStep, selectedDoctor, filteredDoctors, selectDoctor]);
    const totalDueForCta = quote ? bookedChargeAmount(quote) : 0;
    const showPayOnlineCta = onlinePaymentAvailable && totalDueForCta > 0;
    const selectedPaymentMethod = useUnifiedStore((s) => s.selectedPaymentMethod);
    const manualMethodCount = useUnifiedStore((s) => s.availablePaymentMethods.length);
    const pickerVisible = totalDueForCta > 0 && (showPayOnlineCta || manualMethodCount > 0);
    const followUpOfferPossible = config.bookingMode !== "qms" &&
        (followUpGrid ||
            (!selectedPack &&
                !selectedActiveSessionPack &&
                patientTab === "existing" &&
                !!selectedPatient &&
                !!followUpEligibility?.some((e) => e.patientId === Number(selectedPatient.id) &&
                    e.eligibility?.eligible &&
                    e.eligibility.parentAppointmentId != null)));
    const paymentChoiceForBar = pickerVisible
        ? selectedPaymentMethod.kind
        : undefined;
    const claimDraftReference = useUnifiedStore((s) => s.claimDraftReference);
    const claimDraftProof = useUnifiedStore((s) => s.claimDraftProof);
    const manualClaimIncomplete = paymentChoiceForBar === "MANUAL" &&
        claimDraftReference.trim().length === 0 &&
        claimDraftProof === null;
    const confirmedBooking = currentStep === "success" ? bookedAppointment : null;
    const claimCharge = confirmedBooking?.charge ?? quote;
    const claimDue = claimCharge ? bookedChargeAmount(claimCharge) : 0;
    const claimCurrency = claimCharge && claimCharge.kind !== "ACTIVE_PACK"
        ? claimCharge.currency
        : "";
    const claimAvailable = manualMethodCount > 0 &&
        claimDue > 0 &&
        paymentStatus !== "success" &&
        appointmentId != null;
    const [claimSheetOpen, setClaimSheetOpen] = React.useState(false);
    const stepIndicatorLabels = useMemo(() => [
        otpChannel === "email"
            ? t("stepVerifyEmail", "Verify your Email")
            : t(config.stepIndicator[0].key, config.stepIndicator[0].fallback),
        t(config.stepIndicator[1].key, config.stepIndicator[1].fallback),
        t(config.stepIndicator[2].key, config.stepIndicator[2].fallback),
    ], [config, t, otpChannel]);
    const notifiedBookingRef = React.useRef(null);
    React.useEffect(() => {
        if (currentStep !== "success" && currentStep !== "queue-confirmation") {
            return;
        }
        const identity = String(appointmentId ?? queueToken?.tokenNumber ?? paymentAppointmentRef ?? "");
        if (!identity || notifiedBookingRef.current === identity)
            return;
        notifiedBookingRef.current = identity;
        onSuccess?.({
            appointmentId,
            appointmentRef: paymentAppointmentRef,
            queueToken,
            paymentStatus,
            paymentMethod: selectedPaymentMethod,
        });
    }, [
        currentStep,
        appointmentId,
        queueToken,
        paymentAppointmentRef,
        paymentStatus,
        selectedPaymentMethod,
        onSuccess,
    ]);
    const loadError = workspaceError;
    if (!loadError && (workspaceLoading || visibleDoctorIds === null)) {
        return (_jsxs(Box, { className: "medos-booking-widget", sx: outerSx(maxWidth, maxHeight, height, isMobile), "aria-busy": "true", children: [_jsx(GlobalStyles, { styles: globalStyles }), _jsx(Box, { sx: {
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: isMobile ? 1.25 : 1.75,
                        overflow: "hidden",
                        boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                    }, children: _jsx(WidgetSkeleton, { isMobile: !!isMobile }) })] }));
    }
    if (loadError) {
        return (_jsx(Box, { className: "medos-booking-widget", sx: {
                ...outerSx(maxWidth, maxHeight, height, isMobile),
                alignItems: "center",
                justifyContent: "center",
            }, role: "alert", children: _jsxs(Stack, { alignItems: "center", spacing: 1.75, sx: {
                    maxWidth: 380,
                    p: 4,
                    textAlign: "center",
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: isMobile ? 1.25 : 1.75,
                    boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                }, children: [_jsx(Box, { sx: {
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "error.light",
                            color: "error.main",
                        }, children: _jsx(ErrorOutlineIcon, { sx: { fontSize: 32 } }) }), _jsxs(Stack, { spacing: 0.5, alignItems: "center", children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700 }, children: t("somethingWentWrong", "Something went wrong") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: loadError })] }), retryLoad && (_jsx(Button, { variant: "contained", size: "small", onClick: retryLoad, startIcon: _jsx(RefreshIcon, { sx: { fontSize: 16 } }), sx: { mt: 0.5, borderRadius: "50px", px: 2 }, children: t("tryAgain", "Try again") }))] }) }));
    }
    const claimPrompt = claimAvailable && claimStatus !== "submitted" ? (_jsx(Box, { sx: { px: { xs: 2.5, sm: 3 }, pb: 3, textAlign: "center" }, children: _jsx(Button, { variant: "outlined", onClick: () => setClaimSheetOpen(true), children: t("iHavePaid", "I've completed the payment") }) })) : claimStatus === "submitted" ? (_jsx(Box, { sx: { px: { xs: 2.5, sm: 3 }, pb: 3 }, children: _jsx(Alert, { severity: "warning", children: t("claimPendingBanner", "Payment pending verification — keep your reference handy.") }) })) : null;
    const renderStepContent = () => {
        switch (currentStep) {
            case "phone":
                return (_jsx(PhoneStep, { state: bridgedState, dispatch: dispatch, onSendOtp: handleSendOtp }));
            case "otp":
                return (_jsx(OtpStep, { state: bridgedState, dispatch: dispatch, onVerifyOtp: handleVerifyOtp, onSendOtp: handleSendOtp, onChangeNumber: handleChangeNumber }));
            case "patient-selection":
                return (_jsx(PatientSelectionStep, { state: bridgedState, dispatch: dispatch, showSessionPacks: config.showSessionPacks, selectedDuration: effectiveDuration }));
            case "summary":
                return (_jsxs(Box, { sx: { flex: 1, minHeight: 0, overflowY: "auto" }, children: [_jsx(SummaryStep, { state: bridgedState, config: {
                                summaryCardTitle: t(config.summaryCardTitle.key, config.summaryCardTitle.fallback),
                                showFeeInSummary: config.showFeeInSummary,
                                slotTimeFormat: config.slotTimeFormat,
                                showDuration: config.showDuration,
                            } }), (followUpOfferPossible || pickerVisible) && (_jsxs(Box, { sx: {
                                px: { xs: 2.5, sm: 3 },
                                pb: { xs: 2.5, sm: 3 },
                                mt: { xs: -2.5, sm: -3.5 },
                                maxWidth: 728,
                                mx: "auto",
                            }, children: [followUpOfferPossible && (_jsx(FollowUpOffer, { regularVisit: planQuotes.singleVisit })), pickerVisible && (_jsx(PaymentMethodPicker, { showOnline: showPayOnlineCta, paymentTimeoutMinutes: workspace?.paymentTimeoutMinutes }))] }))] }));
            case "success":
                return (_jsxs(_Fragment, { children: [_jsx(SuccessStep, { state: bridgedState, booked: bookedAppointment, config: {
                                slotTimeFormat: config.slotTimeFormat,
                                showDuration: config.showDuration,
                            } }), claimPrompt] }));
            case "queue-confirmation":
                return queueToken ? (_jsxs(_Fragment, { children: [_jsx(QueueConfirmationStep, { token: queueToken }), claimPrompt] })) : null;
            case "payment-pending":
                return (_jsx(PaymentPendingStep, { status: paymentStatus, sessionRef: paymentSessionRef, checkoutUrl: paymentCheckoutUrl, unavailable: paymentUnavailable, popup: paymentPopup, error: paymentError, failureReason: paymentFailureReason, onFinish: finishOnlinePayment, onRetry: handleRetryOnlinePayment, onCancel: handleCancelOnlinePayment, onOpenCheckout: handleReopenCheckout, onRecheck: recheckOnlinePayment, onRebook: handleBookAnother }));
            case "schedule":
            default: {
                if (noVisibleDoctors) {
                    return (_jsx(ScheduleEmptyState, { title: t("noDoctorsAvailable", "No doctors available"), subtitle: t("noDoctorsAvailableHint", "There are no bookable doctors at the moment.") }));
                }
                if (!selectedDoctor && filteredDoctors.length === 0) {
                    return (_jsx(ScheduleEmptyState, { title: t("noDoctorsMatch", "No doctors match"), subtitle: t("noDoctorsMatchHint", "Try a different location or specialization.") }));
                }
                if (doctorHasNoBookableMode) {
                    return (_jsx(ScheduleEmptyState, { title: t("bookingUnavailable", "Booking unavailable"), subtitle: t("bookingUnavailableHint", "This doctor is not accepting online or in-person appointments right now.") }));
                }
                if (doctorModeUnpriced) {
                    return (_jsx(ScheduleEmptyState, { title: t("bookingUnavailable", "Booking unavailable"), subtitle: t("modeUnpricedHint", "This consultation type has not been priced yet. Please try another option or contact the clinic.") }));
                }
                return (_jsxs(_Fragment, { children: [followUpGrid && (_jsx(Alert, { severity: "info", sx: { mx: { xs: 2, sm: 3 }, mt: 2 }, action: _jsx(Button, { color: "inherit", size: "small", onClick: leaveFollowUpGrid, children: t("bookRegularVisitInstead", "Book a regular visit instead") }), children: t("choosingFollowUpSlot", "Choose a time for your follow-up visit.") })), _jsx(SchedulePanel, { state: bridgedState, dispatch: dispatch, isMobile: isMobile, compactLayout: true, onRetrySlots: slotsQuery.refetch, slotsHeadingLabel: t(config.slotsHeading.key, config.slotsHeading.fallback), slotsLoadingLabel: t(config.slotsLoading.key, config.slotsLoading.fallback), slotsEmptyLabel: t(config.slotsEmpty.key, config.slotsEmpty.fallback), showDuration: config.showDuration, consultationMode: consultationMode, availableModes: doctorConsultationModes, onConsultationModeChange: showConsultationToggle ? setConsultationMode : undefined, availableDurations: followUpGrid ? EMPTY_NUMBER_LIST : availableDurations, selectedDuration: selectedDuration, onDurationChange: setSelectedDuration }), !packsOwnedByCard && packsElement] }));
            }
        }
    };
    const stepIndicatorStep = currentStep === "queue-confirmation"
        ? "success"
        : currentStep === "payment-pending"
            ? "summary"
            : currentStep;
    const showHeaderFilters = isScheduleStep &&
        !calendarOnly &&
        !!workspace &&
        !noVisibleDoctors &&
        (hasMultipleLocations || showSpecializationFilter);
    const heroFiltersConfig = showHeaderFilters
        ? {
            consultationMode,
            specializationFilter,
            locationFilter,
            specializations: specializationOptions,
            locations: visibleLocations,
            showConsultationMode: false,
            availableModes: doctorConsultationModes,
            allowAllLocations: false,
            showFullAddress: true,
            showLocation: hasMultipleLocations,
            showSpecialization: showSpecializationFilter,
            onConsultationModeChange: setConsultationMode,
            onSpecializationChange: handleSelectSpecialization,
            onLocationChange: (v) => handleSelectLocation(Number(v)),
        }
        : undefined;
    return (_jsxs(Box, { className: "medos-booking-widget", sx: outerSx(maxWidth, maxHeight, height, isMobile), children: [_jsx(GlobalStyles, { styles: globalStyles }), !calendarOnly && isScheduleStep && (_jsx(BookingHeroHeader, { variant: config.isQms ? "queue" : "appointment", isMobile: isMobile, filters: heroFiltersConfig })), doctorListPlacement === "top" && (_jsxs(Box, { sx: {
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: isMobile ? 1.25 : 1.75,
                    boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                    overflow: "hidden",
                    flexShrink: 0,
                }, children: [_jsx(DoctorFinderBar, { isMobile: isMobile, searchQuery: searchQuery, onSearchChange: setSearchQuery, specializations: specializationOptions, specializationFilter: specializationFilter, onSpecializationChange: handleSelectSpecialization, showSpecializationTabs: showSpecializationFilter, hideBottomBorder: true }), filteredDoctors.length > 0 && (_jsx(DoctorStripUnified, { filteredDoctors: filteredDoctors, selectedDoctor: selectedDoctor, doctorDisplayByDoctorId: doctorDisplayByDoctorId, isMobile: isMobile, onSelectDoctor: selectDoctor }))] })), _jsx(StepIndicatorUnified, { currentStep: stepIndicatorStep, labels: stepIndicatorLabels }), isSideBySide ? (_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: isMobile ? 1.25 : 1.75,
                    boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                }, children: [_jsxs(Box, { sx: {
                            display: "flex",
                            flex: 1,
                            minHeight: 0,
                            minWidth: 0,
                            overflow: "hidden",
                        }, children: [_jsxs(Box, { sx: {
                                    flex: `0 0 ${DOCTOR_LIST_COLUMN_WIDTH}px`,
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }, children: [_jsx(DoctorFinderBar, { isMobile: true, searchQuery: searchQuery, onSearchChange: setSearchQuery, specializations: specializationOptions, specializationFilter: specializationFilter, onSpecializationChange: handleSelectSpecialization, showSpecializationTabs: showSpecializationFilter }), _jsx(DoctorListColumn, { filteredDoctors: filteredDoctors, selectedDoctor: selectedDoctor, doctorDisplayByDoctorId: doctorDisplayByDoctorId, consultationMode: consultationMode, feeAudience: visitorFeeAudience, onSelectDoctor: selectDoctor, emptyLabel: t("noDoctorsFound", "No doctors found"), groupLabel: t("selectDoctor", "Select Doctor") })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsx(Fade, { in: true, appear: true, timeout: prefersReducedMotion ? 0 : { enter: 260, exit: 120 }, children: _jsxs(Box, { sx: {
                                        flex: "1 1 500px",
                                        minWidth: 0,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                    }, children: [showProfileHeaderBand && (_jsx(DoctorCard, { doctor: selectedDoctor, city: selectedDoctorCity, isSelected: true, onSelect: () => { }, solo: true, variant: "header", consultationMode: consultationMode, feeAudience: visitorFeeAudience, display: selectedDoctorDisplay })), renderStepContent()] }) }, currentStep)] }), packsElement && _jsx(Box, { sx: { flexShrink: 0 }, children: packsElement })] })) : showProfilePanel ? (_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: isMobile ? 1.25 : 1.75,
                    boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                }, children: [_jsxs(Box, { sx: {
                            display: "flex",
                            flex: 1,
                            minHeight: 0,
                            minWidth: 0,
                            overflow: "hidden",
                        }, children: [_jsx(Box, { sx: {
                                    flex: "1 1 34%",
                                    minWidth: 230,
                                    maxWidth: 390,
                                    overflow: "hidden",
                                    position: "relative",
                                }, children: _jsx(Box, { sx: { position: "absolute", inset: 0, display: "flex" }, children: _jsx(DoctorCard, { doctor: selectedDoctor, city: selectedDoctorCity, isSelected: true, onSelect: () => { }, solo: true, consultationMode: consultationMode, feeAudience: visitorFeeAudience, display: selectedDoctorDisplay }) }) }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsx(Fade, { in: true, appear: true, timeout: prefersReducedMotion ? 0 : { enter: 260, exit: 120 }, children: _jsx(Box, { sx: {
                                        flex: "1 1 500px",
                                        minWidth: 0,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                    }, children: renderStepContent() }) }, currentStep)] }), packsElement && _jsx(Box, { sx: { flexShrink: 0 }, children: packsElement })] })) : !isScheduleStep ? (_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: isMobile ? 1.25 : 1.75,
                    boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                }, children: [!calendarOnly && (_jsx(WidgetHeader, { variant: config.isQms ? "queue" : "appointment", isMobile: isMobile, onBack: headerBackHandler, compact: true, reserveBackSpace: false, showLanguageSelector: false, showDivider: false })), _jsx(Fade, { in: true, appear: true, timeout: prefersReducedMotion ? 0 : { enter: 260, exit: 120 }, children: _jsx(Box, { sx: {
                                flex: "1 1 500px",
                                minWidth: 0,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                            }, children: renderStepContent() }) }, currentStep)] })) : (_jsx(Box, { sx: {
                    display: "flex",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    minWidth: 0,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: isMobile ? 1.25 : 1.75,
                    boxShadow: "0px 4px 4px 0px rgba(20, 71, 230, 0.08)",
                }, children: _jsx(Fade, { in: true, appear: true, timeout: prefersReducedMotion ? 0 : { enter: 260, exit: 120 }, children: _jsx(Box, { sx: {
                            flex: "1 1 500px",
                            minWidth: 0,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                        }, children: renderStepContent() }) }, currentStep) })), _jsx(BottomBar, { state: bridgedState, dispatch: dispatch, onBook: handleBook, onContinueFromForm: continueFromForm, onContinue: continueToSummary, onPayAtClinic: handlePayAtClinic, onConfirmAppointment: handleConfirmAppointment, onGoBack: goBack, onBookAnother: handleBookAnother, onBackToHome: handleBookAnother, isMobile: isMobile, hideBackButton: true, systemConfig: config, onPayOnline: handlePayOnline, showPayOnline: showPayOnlineCta, paymentChoice: paymentChoiceForBar, primaryActionBlocked: currentStep === "schedule"
                    ? scheduleBookBlocked
                    : summaryBookBlocked || manualClaimIncomplete, pricePending: pricePending }), claimAvailable && appointmentId != null && (_jsx(PaymentClaimSheet, { open: claimSheetOpen, onClose: () => setClaimSheetOpen(false), appointmentId: appointmentId, amount: claimDue, currency: claimCurrency })), pendingTimezone && (_jsx(TimezoneChangeDialog, { pendingTimezone: pendingTimezone, detectedTimezone: detectedTimezone, onCancel: cancelTimezoneChange, onConfirm: confirmTimezoneChange }))] }));
}
function outerSx(maxWidth, maxHeight, height, isMobile) {
    return {
        fontFamily: "inherit",
        display: "flex",
        flexDirection: "column",
        height: height ?? "100%",
        maxHeight: maxHeight ?? undefined,
        maxWidth: maxWidth ?? 1040,
        mx: "auto",
        bgcolor: "transparent",
        color: "text.primary",
        fontSize: 14,
        lineHeight: 1.4,
        WebkitFontSmoothing: "antialiased",
        overflow: "hidden",
        position: "relative",
        gap: isMobile ? 1.25 : 1.75,
        p: isMobile ? 0.75 : 1.5,
    };
}
function ScheduleEmptyState({ title, subtitle, }) {
    return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", spacing: 1.25, sx: {
            flex: 1,
            minHeight: { xs: 320, md: 460 },
            p: 4,
            textAlign: "center",
        }, children: [_jsx(Box, { sx: {
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "action.hover",
                    color: "text.secondary",
                    mb: 0.5,
                }, children: _jsx(SearchOffIcon, { sx: { fontSize: 30 } }) }), _jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700 }, children: title }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { maxWidth: 280, lineHeight: 1.5 }, children: subtitle })] }));
}
function WidgetSkeleton({ isMobile }) {
    return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                    px: isMobile ? 1.5 : 2.25,
                    py: isMobile ? 1 : 1.25,
                    borderBottom: 1,
                    borderColor: "divider",
                    flexShrink: 0,
                }, children: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", spacing: 1, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Skeleton, { variant: "text", width: 180, height: 24 }), !isMobile && _jsx(Skeleton, { variant: "text", width: 260, height: 16 })] }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { flexShrink: 0 }, children: [_jsx(Skeleton, { variant: "rounded", width: 110, height: 32, sx: { borderRadius: "50px" } }), _jsx(Skeleton, { variant: "rounded", width: 48, height: 32, sx: { borderRadius: "50px" } })] })] }) }), _jsx(Box, { sx: {
                    px: isMobile ? 1.5 : 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                    flexShrink: 0,
                }, children: _jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Skeleton, { variant: "rounded", width: 130, height: 30, sx: { borderRadius: "50px" } }), _jsx(Skeleton, { variant: "rounded", width: 150, height: 30, sx: { borderRadius: "50px" } })] }) }), _jsxs(Box, { sx: { display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }, children: [!isMobile && (_jsx(Box, { sx: {
                            flex: "1 1 30%",
                            minWidth: 230,
                            maxWidth: 390,
                            borderRight: 1,
                            borderColor: "divider",
                            p: 2.5,
                        }, children: _jsxs(Stack, { alignItems: "center", spacing: 1.25, children: [_jsx(Skeleton, { variant: "circular", width: 96, height: 96 }), _jsx(Skeleton, { variant: "text", width: 140, height: 22 }), _jsx(Skeleton, { variant: "text", width: 100, height: 16 }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { mt: 1 }, children: [_jsx(Skeleton, { variant: "rounded", width: 56, height: 24 }), _jsx(Skeleton, { variant: "rounded", width: 56, height: 24 }), _jsx(Skeleton, { variant: "rounded", width: 56, height: 24 })] }), _jsxs(Box, { sx: { width: "100%", mt: 1.5 }, children: [_jsx(Skeleton, { variant: "text", width: "80%" }), _jsx(Skeleton, { variant: "text", width: "70%" }), _jsx(Skeleton, { variant: "text", width: "75%" })] })] }) })), _jsxs(Box, { sx: { flex: 1, minWidth: 0, p: isMobile ? 1.5 : 2 }, children: [_jsx(Skeleton, { variant: "rounded", height: isMobile ? 260 : 300, sx: { borderRadius: 1.5 } }), _jsx(Box, { sx: {
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${isMobile ? 3 : 4}, 1fr)`,
                                    gap: 0.75,
                                    mt: 1.5,
                                }, children: Array.from({ length: isMobile ? 6 : 8 }).map((_, i) => (_jsx(Skeleton, { variant: "rounded", height: 30 }, i))) })] })] }), _jsx(Box, { sx: {
                    py: 1,
                    borderTop: 1,
                    borderColor: "divider",
                    flexShrink: 0,
                    textAlign: "center",
                }, "aria-live": "polite", children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", justifyContent: "center", children: [_jsx(CircularProgress, { size: 13, thickness: 5 }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { fontWeight: 600 }, children: "Loading schedules" })] }) })] }));
}
function StepIndicatorUnified({ currentStep, labels, }) {
    return _jsx(StepIndicator, { currentStep: currentStep, labels: labels });
}
function DoctorListColumn({ filteredDoctors, selectedDoctor, doctorDisplayByDoctorId, consultationMode, feeAudience, onSelectDoctor, emptyLabel, groupLabel, }) {
    const selectedRef = React.useRef(null);
    const selectedDoctorId = selectedDoctor?.id ?? null;
    React.useEffect(() => {
        const node = selectedRef.current;
        if (!node || typeof node.scrollIntoView !== "function")
            return;
        node.scrollIntoView({ block: "nearest", inline: "nearest" });
    }, [selectedDoctorId]);
    if (filteredDoctors.length === 0) {
        return (_jsx(Box, { sx: {
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2.5,
            }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { textAlign: "center", lineHeight: 1.5 }, children: emptyLabel }) }));
    }
    return (_jsx(Stack, { role: "group", "aria-label": groupLabel, spacing: 0.75, sx: { flex: 1, minHeight: 0, overflowY: "auto", p: 1.25 }, children: filteredDoctors.map(({ doctor, addressId, city }) => {
            const isSelected = selectedDoctorId === doctor.id;
            return (_jsx(Box, { ref: isSelected ? selectedRef : undefined, children: _jsx(DoctorCard, { doctor: doctor, city: city, isSelected: isSelected, onSelect: () => onSelectDoctor(doctor, addressId), consultationMode: consultationMode, feeAudience: feeAudience, display: getDisplayForDoctor(doctor, doctorDisplayByDoctorId) }) }, `${doctor.id}-${addressId}`));
        }) }));
}
function DoctorStripUnified({ filteredDoctors, selectedDoctor, doctorDisplayByDoctorId, isMobile, onSelectDoctor, }) {
    return (_jsx(Box, { sx: {
            bgcolor: "background.paper",
            flexShrink: 0,
            px: isMobile ? 1.5 : 2,
            py: isMobile ? 1 : 1.2,
        }, children: _jsx(Box, { className: "medos-hscroll", sx: { display: "flex", overflowX: "auto", gap: isMobile ? 1 : 1.25 }, children: filteredDoctors.map(({ doctor, addressId }) => {
                const display = getDisplayForDoctor(doctor, doctorDisplayByDoctorId);
                return (_jsx(DoctorSquareCard, { display: display, isSelected: selectedDoctor?.id === doctor.id, onSelect: () => onSelectDoctor(doctor, addressId), mobile: isMobile }, `${doctor.id}-${addressId}`));
            }) }) }));
}
